import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/revenue`;

interface WaterfallPeriod { period: string; opening_deferred: number; recognized: number; closing_deferred: number; }
interface ContractSummary { contract_id: number; contract_number: string; customer_name: string; entity_code: string; total_value: number; total_recognized: number; total_deferred: number; }
interface Summary { total_contracts: number; total_transaction_price: number; total_recognized: number; total_deferred: number; pct_recognized: number; by_recognition_method: Record<string, number>; }
interface PeriodRec { period: string; recognized: number; cumulative: number; }
interface EntityRec { entity_code: string; recognized: number; allocated: number; deferred: number; pct_recognized: number; contract_count: number; }
interface ForecastPeriod { period: string; scheduled: number; cumulative: number; line_count: number; }
interface ForecastContract { contract_id: number; contract_number: string; customer_name: string; entity_code: string; total_forecast: number; }
interface ForecastData { horizon_months: number; from_period: string; to_period: string; total_forecast: number; by_period: ForecastPeriod[]; by_entity: { entity_code: string; scheduled: number }[]; by_contract: ForecastContract[]; }

const waterfallCols: Column[] = [
  { key: 'period', label: 'Period', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'opening_deferred', label: 'Opening Deferred', align: 'right', render: (v: number) => formatAmount(v) },
  { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)', fontWeight: 600 }}>{formatAmount(v)}</span> },
  { key: 'closing_deferred', label: 'Closing Deferred', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
];

const contractCols: Column[] = [
  { key: 'contract_number', label: 'Contract', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
  { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
  { key: 'entity_code', label: 'Entity', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{v || '-'}</span> },
  { key: 'total_value', label: 'Total Value', align: 'right', render: (v: number) => formatAmount(v) },
  { key: 'total_recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
  { key: 'total_deferred', label: 'Deferred', align: 'right', render: (v: number) => <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{formatAmount(v)}</span> },
];

const periodCols: Column[] = [
  { key: 'period', label: 'Period', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)', fontWeight: 600 }}>{formatAmount(v)}</span> },
  { key: 'cumulative', label: 'Cumulative', align: 'right', render: (v: number) => formatAmount(v) },
  { key: 'line_count', label: 'Lines', align: 'right', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span> },
];

const entityCols: Column[] = [
  { key: 'entity_code', label: 'Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'contract_count', label: 'Contracts' },
  { key: 'allocated', label: 'Allocated', align: 'right', render: (v: number) => formatAmount(v) },
  { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
  { key: 'deferred', label: 'Deferred', align: 'right', render: (v: number) => <span style={{ color: 'var(--warning)' }}>{formatAmount(v)}</span> },
  { key: 'pct_recognized', label: '% Recognized', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{v}%</span> },
];

const forecastPeriodCols: Column[] = [
  { key: 'period', label: 'Period', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'scheduled', label: 'Scheduled', align: 'right', render: (v: number) => <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{formatAmount(v)}</span> },
  { key: 'cumulative', label: 'Cumulative', align: 'right', render: (v: number) => formatAmount(v) },
  { key: 'line_count', label: 'Lines', align: 'right', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span> },
];

const forecastContractCols: Column[] = [
  { key: 'contract_number', label: 'Contract', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
  { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
  { key: 'entity_code', label: 'Entity' },
  { key: 'total_forecast', label: 'Forecast', align: 'right', render: (v: number) => <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{formatAmount(v)}</span> },
];

export default function RevenueWaterfall() {
  const { entity } = useGlobalEntity();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [periods, setPeriods] = useState<WaterfallPeriod[]>([]);
  const [contracts, setContracts] = useState<ContractSummary[]>([]);
  const [byPeriod, setByPeriod] = useState<PeriodRec[]>([]);
  const [byEntity, setByEntity] = useState<EntityRec[]>([]);
  const [forecast, setForecast] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('waterfall');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, wfRes, prRes, enRes, fcRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/summary`, entity)),
        authFetch(withEntityParam(`${API}/waterfall`, entity)),
        authFetch(withEntityParam(`${API}/by-period`, entity)),
        authFetch(withEntityParam(`${API}/by-entity`, entity)),
        authFetch(withEntityParam(`${API}/reconciliation/forecast`, entity)),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (wfRes.ok) { const d = await wfRes.json(); setPeriods(d.periods || []); setContracts(d.contracts || []); }
      if (prRes.ok) { const d = await prRes.json(); setByPeriod(d.periods || []); }
      if (enRes.ok) { const d = await enRes.json(); setByEntity(d.entities || []); }
      if (fcRes.ok) setForecast(await fcRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: 'waterfall', label: 'Deferred Waterfall' },
    { key: 'period', label: 'By Period' },
    { key: 'entity', label: 'By Entity' },
    { key: 'forecast', label: 'Forecast' },
  ];

  return (
    <PageLayout title="Revenue Waterfall" subtitle="Deferred revenue waterfall, recognition by period and entity">
      {summary && (
        <KpiGrid columns={5} items={[
          { label: 'Contracts', value: summary.total_contracts },
          { label: 'Total Value', value: formatAmountCompact(summary.total_transaction_price), color: 'var(--accent)' },
          { label: 'Recognized', value: formatAmountCompact(summary.total_recognized), color: 'var(--positive)' },
          { label: 'Deferred', value: formatAmountCompact(summary.total_deferred), color: 'var(--warning)' },
          { label: 'Progress', value: `${summary.pct_recognized}%`, color: 'var(--accent)' },
        ]} />
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : tab === 'waterfall' ? (
        <>
          {periods.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Period Waterfall</div>
              <DataTable columns={waterfallCols} rows={periods} rowKey="period" emptyMessage="No waterfall data." />
            </div>
          )}
          {contracts.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Contract</div>
              <DataTable columns={contractCols} rows={contracts} rowKey="contract_id" emptyMessage="No contract data." />
            </div>
          )}
          {periods.length === 0 && contracts.length === 0 && (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No waterfall data. Generate recognition schedules first.</div>
          )}
        </>
      ) : tab === 'period' ? (
        <DataTable columns={periodCols} rows={byPeriod} rowKey="period" emptyMessage="No recognition data by period." />
      ) : tab === 'entity' ? (
        <DataTable columns={entityCols} rows={byEntity} rowKey="entity_code" emptyMessage="No recognition data by entity." />
      ) : (
        <>
          {forecast && (
            <KpiGrid columns={3} items={[
              { label: 'Horizon', value: `${forecast.from_period} → ${forecast.to_period}` },
              { label: 'Forecast Revenue', value: formatAmountCompact(forecast.total_forecast), color: 'var(--accent)' },
              { label: 'Contracts', value: forecast.by_contract.length },
            ]} />
          )}
          {forecast && forecast.by_period.length > 0 ? (
            <>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Next {forecast.horizon_months} Months</div>
                <DataTable columns={forecastPeriodCols} rows={forecast.by_period} rowKey="period" emptyMessage="No scheduled recognition in horizon." />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Contract</div>
                <DataTable columns={forecastContractCols} rows={forecast.by_contract} rowKey="contract_id" emptyMessage="No contracts with scheduled recognition in horizon." />
              </div>
            </>
          ) : (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No forward recognition scheduled in the next 12 months.</div>
          )}
        </>
      )}
    </PageLayout>
  );
}
